import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function WomenInitiativesManager() {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingInitiative, setEditingInitiative] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    level: 'central',
    icon: 'people',
    color: '#FF1493',
    url: '',
    order: 1,
  });

  useEffect(() => {
    loadInitiatives();
  }, []);

  const loadInitiatives = async () => {
    try {
      const q = query(collection(db, 'womenInitiatives'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const initiativesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInitiatives(initiativesData);
    } catch (error) {
      console.error('Error loading initiatives:', error);
      alert('Failed to load initiatives');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingInitiative) {
        await updateDoc(doc(db, 'womenInitiatives', editingInitiative.id), {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
        alert('Initiative updated successfully!');
      } else {
        await addDoc(collection(db, 'womenInitiatives'), {
          ...formData,
          createdAt: new Date().toISOString(),
        });
        alert('Initiative added successfully!');
      }
      setShowModal(false);
      setEditingInitiative(null);
      resetForm();
      loadInitiatives();
    } catch (error) {
      console.error('Error saving initiative:', error);
      alert('Failed to save initiative');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (initiative) => {
    setEditingInitiative(initiative);
    setFormData({
      title: initiative.title,
      description: initiative.description,
      level: initiative.level,
      icon: initiative.icon,
      color: initiative.color,
      url: initiative.url || '',
      order: initiative.order,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this initiative?')) return;

    try {
      await deleteDoc(doc(db, 'womenInitiatives', id));
      alert('Initiative deleted successfully!');
      loadInitiatives();
    } catch (error) {
      console.error('Error deleting initiative:', error);
      alert('Failed to delete initiative');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      level: 'central',
      icon: 'people',
      color: '#FF1493',
      url: '',
      order: 1,
    });
  };

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Women Initiatives Management</h2>
            <button
              onClick={() => {
                setEditingInitiative(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Add New Initiative
            </button>
          </div>

          {loading && <p>Loading...</p>}

          {!loading && initiatives.length === 0 && (
            <p>No initiatives found. Add your first initiative!</p>
          )}

          {!loading && initiatives.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Level</th>
                  <th>Icon</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Color</th>
                  <th>URL</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {initiatives.map((initiative) => (
                  <tr key={initiative.id}>
                    <td>{initiative.order}</td>
                    <td>
                      <span className={`badge ${initiative.level === 'central' ? 'badge-primary' : 'badge-secondary'}`}>
                        {initiative.level === 'central' ? 'Central' : 'State'}
                      </span>
                    </td>
                    <td>{initiative.icon}</td>
                    <td style={{ maxWidth: '200px' }}>{initiative.title}</td>
                    <td style={{ maxWidth: '300px', fontSize: '12px' }}>{initiative.description}</td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '30px',
                          height: '30px',
                          backgroundColor: initiative.color,
                          borderRadius: '4px',
                        }}
                      ></span>
                    </td>
                    <td style={{ fontSize: '11px' }}>
                      {initiative.url ? (
                        <a href={initiative.url} target="_blank" rel="noopener noreferrer">Link</a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(initiative)}
                        className="btn btn-secondary"
                        style={{ marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(initiative.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="info-box">
          <h3>Initiative Guidelines</h3>
          <p><strong>Level:</strong> Central (national programs) or State (state-level programs)</p>
          <p><strong>Icons:</strong> Use Ionicons names - people, heart, school, wallet, shield, trophy, gift, flame, ribbon, star, cash, etc.</p>
          <p><strong>Color:</strong> Choose a unique color for each initiative for easy identification</p>
          <p><strong>URL:</strong> Optional - Add official government website URL for the initiative</p>
          <a href="https://ionic.io/ionicons" target="_blank" rel="noopener noreferrer">
            View all icons →
          </a>
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingInitiative ? 'Edit Initiative' : 'Add New Initiative'}
              </h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Beti Bachao Beti Padhao"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the initiative and its benefits"
                  rows="4"
                  required
                />
              </div>

              <div className="form-group">
                <label>Level *</label>
                <select
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  required
                >
                  <option value="central">Central Government</option>
                  <option value="state">State Government</option>
                </select>
              </div>

              <div className="form-group">
                <label>Icon Name *</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="e.g., people, heart, school, wallet"
                  required
                />
              </div>

              <div className="form-group">
                <label>Color *</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Official Website URL (Optional)</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://wcd.nic.in/..."
                />
              </div>

              <div className="form-group">
                <label>Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingInitiative ? 'Update Initiative' : 'Add Initiative'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingInitiative(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default WomenInitiativesManager;
